import { useMemo, useState } from "react";

import { useCan, useRealTimeTypedQuery } from "components";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type { QuestionResult } from "models";
import type { AdvisedCriticalItemsHistoryParams, PaginatedQuestionResults } from "modules/AppointmentDetails/queryKeys";

const useAdvisedCriticalItemsHistoryListeners = (params: AdvisedCriticalItemsHistoryParams) => {
  const listeners = useMemo(() => {
    const advisedCriticalItemsHistoryListeners: WebSocketMessageListener<QuestionResult>[] = [
      {
        model: "QuestionResult",
        action: "upsert",
        callback: ({ data: questionResult }) => {
          const advisedCriticalItemsHistory = typedQueryClient.getQueryData(AppointmentDetailsKeys.advisedCriticalItemsHistory(params));
          if (!advisedCriticalItemsHistory) return;

          const questionResults = [...(advisedCriticalItemsHistory.question_results ?? [])];
          const questionResultIdx = questionResults.findIndex(({ id }) => id === questionResult.id);
          if (questionResultIdx === -1) return;

          typedQueryClient.setQueryData(AppointmentDetailsKeys.advisedCriticalItemsHistory(params), {
            ...advisedCriticalItemsHistory,
            question_results: questionResult.is_hidden_in_history
              ? questionResults.toSpliced(questionResultIdx, 1)
              : questionResults.with(questionResultIdx, { ...questionResults[questionResultIdx], ...questionResult }),
            nb_count:
              questionResult.is_hidden_in_history && advisedCriticalItemsHistory.nb_count > 0
                ? advisedCriticalItemsHistory.nb_count - 1
                : advisedCriticalItemsHistory.nb_count
          });
        }
      }
    ];

    return advisedCriticalItemsHistoryListeners;
  }, [params.appointment_id]);

  return listeners;
};

const sortHistory = (paginatedHistory: PaginatedQuestionResults) => ({
  ...paginatedHistory,
  question_results:
    paginatedHistory.question_results?.sort((a, b) => {
      if (!a.time_car_app || !b.time_car_app || a.time_car_app === b.time_car_app) return 0;
      return b.time_car_app > a.time_car_app ? 1 : -1;
    }) ?? []
});

type UseAdvisedCriticalItemsHistoryProps = {
  car_id?: number;
  appointment_id?: number;
  forceVisibleItems?: boolean;
};

type UseAdvisedCriticalItemsHistoryCountProps = {
  car_id?: number;
  appointment_id?: number;
};

export const useAdvisedCriticalItemsHistory = ({ appointment_id, car_id, forceVisibleItems }: UseAdvisedCriticalItemsHistoryProps) => {
  const canListAdvisedCriticalHistory = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.LeadsAdvisedCriticalHistory);

  const [page, setPage] = useState(1);
  const [showHiddenItems, setShowHiddenItems] = useState(false);

  const listeners = useAdvisedCriticalItemsHistoryListeners({ appointment_id, car_id, page, hidden_items: forceVisibleItems ? false : showHiddenItems });

  const toggleHiddenItems = () => {
    setPage(1);
    setShowHiddenItems(prev => !prev);
  };

  const {
    data: advisedCriticalHistory,
    isFetching,
    refetch
  } = useRealTimeTypedQuery({
    enabled: canListAdvisedCriticalHistory && !!car_id,
    queryKey: AppointmentDetailsKeys.advisedCriticalItemsHistory({ appointment_id, car_id, page, hidden_items: forceVisibleItems ? false : showHiddenItems }),
    queryFn: async ({ baseUrl, endpoint, params }) => (await ApiInstance.post(endpoint, params, baseUrl)).data,
    select: sortHistory,
    listeners
  });

  return {
    page,
    setPage,
    showHiddenItems,
    toggleHiddenItems,
    advisedCriticalHistory,
    loading: isFetching,
    refetchAdvisedCriticalHistory: () => {
      if (!canListAdvisedCriticalHistory) return;

      // this is a workaround because we keep a cache for the first page of visible items for the count of the section,
      // so if you are on hidden items or second page of visible items, and make one visible or convert one so the count reduce
      // the refetch here will only refetch hidden/second page items, and not trigger refetch on the count hook, so this invalidate triggers it
      // it will be removed when we implement properly hidding/showing items instead of reloading, see the TODO in AdvisedCriticalHistoryTable
      if (showHiddenItems || page > 1)
        typedQueryClient.invalidateQueries(AppointmentDetailsKeys.advisedCriticalItemsHistory({ appointment_id, car_id, page: 1, hidden_items: false }));
      refetch();
    }
  };
};

export const useAdvisedCriticalItemsHistoryCount = ({ appointment_id, car_id }: UseAdvisedCriticalItemsHistoryCountProps) =>
  useAdvisedCriticalItemsHistory({ appointment_id, car_id }).advisedCriticalHistory?.nb_count ?? 0;
